<template>
    <div class="input-wrapper">
        <input
            :maxlength="maxlength"
            type="tel"
            v-model="value"
            :name="name"
            :class="['text-input', !isNull(value) && !isUndefined(value) && value !== '' && 'filled']"
            :max="max"
            :min="min"
            :placeholder="placeholder"
            @focus="setPlaceholder"
            @focusout="placeholder = ''"
            @keydown="onKeydown"
            @blur="handleChange"
            ref="inputField"
        />

        <FloatingLabel :label="displayName" :icon="$t('currency')" />
        <div class="error" v-if="overrideDisplayErrorMessage">{{ overrideDisplayErrorMessage }}</div>
    </div>
</template>

<script>
import { useField } from 'vee-validate'
import { moneyFormatter, formatCurrency } from '@js/utilities/number.js'
import { useStore } from 'vuex'
import { toRefs } from 'vue'
import { titleCase } from '@js/utilities/language.js'

// lodash components
import replace from 'lodash/replace'
import toInteger from 'lodash/toInteger'
import isNull from 'lodash/isNull'
import split from 'lodash/split'
import isUndefined from 'lodash/isUndefined'
import _get from 'lodash/get'
import _set from 'lodash/set'
import join from 'lodash/join'

export default {
    props: {
        allowZero: Boolean,
        customErrorMessage: String,
        icon: String,
        label: String,
        max: [Number, String],
        maxlength: Number,
        min: [Number, String],
        name: String,
        validationRules: Object,
        vuexKey: String,
    },
    setup(props) {
        const store = useStore()
        let { validationRules: reactiveValidationRules } = toRefs(props)

        const { errorMessage, handleChange, resetField, validate } = useField(props.name, reactiveValidationRules, {
            label: titleCase(props.name),
        })

        return {
            errorMessage,
            handleChange,
            resetField,
            validate,
            store,
            reactiveValidationRules,
        }
    },
    created() {
        if (this.minAnnualSalary) {
            this.emitter.on(this.name + 'Validation', () => {
                this.setValidationSalaryValue()
            })
        }
    },
    mounted() {
        if (this.value) {
            this.$nextTick(() => {
                this.resetField({
                    value: this.value,
                })
                this.validate()
            })
        }
    },
    data() {
        return {
            placeholder: '',
        }
    },
    computed: {
        value: {
            get() {
                const value = this.store.getters.fieldValue(this.name, this.vuexKey || 'data', !!this.vuexKey)

                return value && moneyFormatter.format(value)
            },
            set(value) {
                value = replace(value, /,/gi, '')
                value = replace(value, /\s/g, '')

                if (!this.allowZero) {
                    value = value !== 0 ? value : ''
                }

                if (value) {
                    value = split(value, '.')[0]

                    value = toInteger(value)
                }

                if (this.vuexKey) {
                    this.store.commit('setDataFieldValue', {
                        name: this.name,
                        value: value,
                        key: this.vuexKey,
                    })
                } else {
                    this.store.dispatch('setFieldValue', {
                        name: this.name,
                        value: value,
                    })
                }

                this.$emit('input', {
                    name: this.name,
                    value: value,
                })

                if (this.store.getters.field(this.name, this.vuexKey || 'data', !!this.vuexKey)?.displayValue) {
                    this.store.dispatch('setDisplayValue', {
                        name: this.name,
                        value: value,
                    })
                }
            },
        },
        displayName: function () {
            return this.label || this.titleCase(this.name)
        },
        serverError: function () {
            return this.store.getters.fieldServerError(this.name)
        },
        displayErrorMessage: function () {
            return this.errorMessage && (this.customErrorMessage || this.errorMessage || this.serverError)
        },
        territory: function () {
            return process.env.TERRITORY
        },
        overrideDisplayErrorMessage: function () {
            if (this.displayErrorMessage && this.errorMessage === 'errorAnnualSalary') {
                return this.$t('form.fields.income.lowAnnualErrorMessage')
            }

            return this.displayErrorMessage
        },
        minAnnualSalary: function () {
            return this.store.getters.field('income', 'data', false, true)?.validationRules?.minAnnualSalary
        },
    },
    methods: {
        isNull: isNull,
        isUndefined: isUndefined,
        setPlaceholder: function () {
            if (this.min && this.max && !this.value) {
                this.placeholder = `${formatCurrency(this.min)} ${this.$t('general.to')} ${formatCurrency(this.max)}`
            } else {
                this.placeholder = ''
            }
        },
        onKeydown: function (event) {
            if (event.key === 'Enter' || event.key === 'Backspace') {
                return
            }

            if (!/[0-9]/.test(event.key)) {
                event.preventDefault()
            }
        },
        focus() {
            this.autoFocus(this.$refs.inputField)
        },
        setValidationSalaryValue() {
            if (this.minAnnualSalary && this.value !== this.minAnnualSalary.value) {
                this.minAnnualSalary.value = this.value
            }
        },
    },
    watch: {
        value(value) {
            this.resetField({
                value: value,
            })

            if (this.minAnnualSalary) {
                this.minAnnualSalary.value = null
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/responsive';

input {
    &::placeholder {
        color: #4b5a7e;
    }
}

.input-wrapper {
    .error {
        @media screen and (min-width: $bp-large) {
            padding: 2px 5px 5px 22px;
        }
    }
}
</style>
